import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import PageShell from '@/components/PageShell';
import SectionHeader from '@/components/SectionHeader';
import PlatformCard from '@/components/PlatformCard';
import VisionValueChain from '@/components/VisionValueChain';
import { PLATFORMS } from '@/lib/platforms';

const PLATFORMS_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'The Mobility Group · Platform Ecosystem',
  description:
    'Eight automotive and mobility platforms operated and backed by The Mobility Group — covering market intelligence, dealer operations, lifecycle data, claims, marketplaces, workshops and parts commerce.',
  url: 'https://themobilitygroup.co/platforms',
  numberOfItems: PLATFORMS.length,
  itemListOrder: 'https://schema.org/ItemListOrderDescending',
  itemListElement: PLATFORMS.map((p, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'SoftwareApplication',
      name: p.name,
      url: p.url,
      applicationCategory: p.category,
      description: p.description,
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', category: p.type === 'built' ? 'Operated platform' : 'Portfolio platform' },
    },
  })),
};

const Platforms = () => {
  return (
    <PageShell>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(PLATFORMS_JSONLD) }}
      />
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="absolute inset-0 radial-glow-cyan opacity-50" />
        <div className="relative px-6 md:px-12 lg:px-16 pt-24 pb-16">
          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-px w-6 bg-amber inline-block" />
              Platform ecosystem
            </span>
          </div>
          <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl text-balance max-w-4xl leading-[1.02]">
            The operating system for the automotive industry.
          </h1>
          <p className="mt-8 max-w-2xl text-zinc-300 text-base md:text-lg leading-relaxed">
            A growing set of market-tested platforms — some we built, some
            we back — already serving OEMs, dealerships, insurers, lenders,
            workshops, parts distributors and vehicle owners at India
            scale.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PLATFORMS.map((p, i) => (
            <PlatformCard key={p.slug} platform={p} index={i} />
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-t border-white/10 bg-black">
        <SectionHeader
          kicker="How they fit together"
          title="One vehicle. One lifecycle. One stack."
          intro="Every platform plugs into a shared spine — chassis-linked vehicle data — so that a lead today becomes a delivered vehicle, a serviced asset, and a claim resolved tomorrow."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/10">
          {[
            ['Acquire', 'DLIP + Oto.bot + TrucksBuses.com', 'From enquiry to delivered deal — with AI copilots on the floor.'],
            ['Understand', 'MotoInsights.in', 'Market share, demand, EV adoption, network planning.'],
            ['Connect', 'VehicleLedger.in + AutoData.Exchange', 'One chassis number, one trusted record.'],
            ['Service', 'BayWise.ai + Autorox.ai', 'Workshop OS: AI scheduling, embedded payments, live floor ops.'],
            ['Protect', 'CarBikeClaims.com', 'Complaints become reliability and defect intelligence.'],
            ['Underwrite', 'AutoData.Exchange + VehicleLedger.in', 'Real-world risk pricing for insurers and lenders.'],
            ['Scale', 'Group-level partnerships', 'OEM, dealer, fleet and global expansion playbooks.'],
          ].map(([phase, p, d]) => (
            <div key={phase} className="bg-ink-900 p-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan mb-3">
                {phase}
              </div>
              <h3 className="font-display text-xl mb-2">{p}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-ink-900 border-t border-white/10">
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl mb-6">
            Want a platform built on this stack?
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-8">
            We co-create platforms through equity partnerships, revenue-share
            models, and joint ventures — for OEMs, large dealer groups,
            insurers, and category creators.
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
          >
            Talk to us
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
};

export default Platforms;
